// Central project catalog. Copy is authored in English and Indonesian;
// other languages fall back to English.
import type { Language } from "@/contexts/LanguageContext";
import { placeholderThumbnail } from "@/lib/placeholderThumbnail";
import fintcuShot from "@/assets/projects/fintcu.jpg";
import sigmahubShot from "@/assets/projects/sigmahub.jpg";
import invoicekuShot from "@/assets/projects/invoiceku.jpg";
import slayHeroesShot from "@/assets/projects/slay-heroes.jpg";
import tcuberAcademyShot from "@/assets/projects/tcuber-academy.jpg";
import paumedikaShot from "@/assets/projects/paumedika.jpg";
import salesTrackerShot from "@/assets/projects/sales-tracker.jpg";
import liveprodCpShot from "@/assets/projects/liveprod-cp.jpg";
import dokumGsc2026Shot from "@/assets/projects/dokum-gsc-2026.jpg";
import sendyTgJobCenterShot from "@/assets/projects/sendy-tg-job-center.jpg";
import cgYa124Shot from "@/assets/projects/cg-ya124.jpg";
import monicaHouseShot from "@/assets/projects/monica-house.jpg";
import tsecureShot from "@/assets/projects/tsecure.jpg";
import tcuAnalyticsShot from "@/assets/projects/tcu-analytics.jpg";
import tcustreamShot from "@/assets/projects/tcustream.jpg";
import tcuProjectShot from "@/assets/projects/tcu-project.jpg";
import cloudCareerShot from "@/assets/projects/cloud-career-accelerator.jpg";
import tcuphotoShot from "@/assets/projects/tcuphoto.jpg";
import blessedSportShot from "@/assets/projects/blessed-sport.jpg";
import olahragaRohaniShot from "@/assets/projects/olahraga-rohani-bareng.jpg";
import pempekHoki88Shot from "@/assets/projects/pempek-hoki-88.jpg";
import cslAirSolutionShot from "@/assets/projects/csl-air-solution.jpg";
import atelierShot from "@/assets/projects/atelier.jpg";
import tcuberAgencyShot from "@/assets/projects/tcuber-agency.jpg";
import tcuberverseShot from "@/assets/projects/tcuberverse.jpg";
import royalChessShot from "@/assets/projects/royal-chess.jpg";
import newYearPhotoBoothShot from "@/assets/projects/new-year-photo-booth.jpg";
import swiftiesCornerShot from "@/assets/projects/swifties-corner.jpg";
import simpleTwitterShot from "@/assets/projects/simple-twitter.jpg";
import webCatalogShot from "@/assets/projects/web-catalog-prototype.jpg";
import habitTrackerShot from "@/assets/projects/habit-tracker.jpg";
import flytcuShot from "@/assets/projects/flytcu.jpg";
import splitbillShot from "@/assets/projects/splitbill.jpg";
import cgScheduleProShot from "@/assets/projects/cg-schedule-pro.jpg";
import ayamJuaraShot from "@/assets/projects/ayam-juara.jpg";
import pelatihanVbaShot from "@/assets/projects/pelatihan-vba-sql-powerbi.jpg";

export interface ProjectCopy {
  title: string;
  description: string;
  about: string;
  keyFeatures: string[];
  challenges: string[];
}

export interface ProjectDefinition {
  id: string;
  link: string | null;
  thumbnail?: string;
  galleryImages?: string[];
  tags: string[];
  category: string;
  role: string;
  duration?: string;
  technologies: string[];
  isInteractive?: boolean;
  copy: { en: ProjectCopy; id: ProjectCopy };
}

export interface ResolvedProject extends Omit<ProjectDefinition, "copy" | "thumbnail">, ProjectCopy {
  thumbnail: string;
  galleryImages: string[];
}

export const EXTRA_PROJECTS: ProjectDefinition[] = [
  {
    id: "fintcu",
    link: "https://fintcu.vercel.app",
    thumbnail: fintcuShot,
    tags: ["Web", "ERP", "Finance", "Next.js"],
    category: "ERP / Business System",
    role: "Full Stack Developer",
    technologies: ["Next.js", "TypeScript", "Prisma", "PostgreSQL", "Tailwind CSS"],
    copy: {
      en: {
        title: "Fintcu ERP",
        description:
          "ERP for service companies: contracts, down payments and installments, VAT, dividends, loans, and bank reconciliation in one finance workspace.",
        about:
          "Fintcu is an ERP built for service-based businesses that need contract-driven billing instead of stock-driven invoicing. It tracks a project from contract to cash: down payment, progress terms, VAT, expenses, loans, dividends, and bank reconciliation, with document numbering handled centrally so every ledger entry stays traceable.",
        keyFeatures: [
          "Contract-based billing with down payment and progress terms",
          "VAT, dividend, and loan calculations in a shared finance module",
          "Bank reconciliation against recorded transactions",
          "Centralised document numbering across modules",
          "Bilingual interface with translated transaction phrases",
        ],
        challenges: [
          "Modelling service contracts and partial payments in a single ledger",
          "Keeping tax and dividend maths consistent across modules",
          "Designing a numbering scheme that survives concurrent writes",
        ],
      },
      id: {
        title: "Fintcu ERP",
        description:
          "ERP untuk perusahaan jasa: kontrak, DP dan termin, PPN, dividen, pinjaman, dan rekonsiliasi bank dalam satu ruang kerja keuangan.",
        about:
          "Fintcu adalah ERP untuk bisnis jasa yang penagihannya berbasis kontrak, bukan berbasis stok. Sistem ini mengikuti proyek dari kontrak sampai kas masuk: DP, termin progres, PPN, biaya, pinjaman, dividen, dan rekonsiliasi bank, dengan penomoran dokumen terpusat agar setiap jurnal tetap bisa ditelusuri.",
        keyFeatures: [
          "Penagihan berbasis kontrak dengan DP dan termin progres",
          "Perhitungan PPN, dividen, dan pinjaman dalam satu modul keuangan",
          "Rekonsiliasi bank terhadap transaksi tercatat",
          "Penomoran dokumen terpusat lintas modul",
          "Antarmuka dua bahasa dengan istilah transaksi yang diterjemahkan",
        ],
        challenges: [
          "Memodelkan kontrak jasa dan pembayaran bertahap dalam satu buku besar",
          "Menjaga konsistensi hitungan pajak dan dividen antar modul",
          "Merancang penomoran yang tetap aman saat penulisan bersamaan",
        ],
      },
    },
  },
  {
    id: "sigmahub",
    link: "https://koophub.vercel.app",
    thumbnail: sigmahubShot,
    tags: ["Web", "Inventory", "Desktop", "Supabase"],
    category: "Inventory Management System",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "Capacitor", "Electron"],
    copy: {
      en: {
        title: "SigmaHub",
        description:
          "Material management system covering item master data, goods receipt and issue transactions, and comprehensive stock reporting.",
        about:
          "SigmaHub centralises warehouse material management. It maintains the item master, records incoming and outgoing transactions, and produces stock and movement reports. The same codebase ships as a web app, an Android build via Capacitor, and a desktop build via Electron.",
        keyFeatures: [
          "Item master data with categories and units",
          "Goods receipt and goods issue transactions",
          "Stock movement and balance reports",
          "Role-based access backed by Supabase",
          "Web, Android, and desktop builds from one codebase",
        ],
        challenges: [
          "Keeping stock balances correct across concurrent transactions",
          "Packaging one React codebase for web, mobile, and desktop",
          "Designing reports that stay readable on small screens",
        ],
      },
      id: {
        title: "SigmaHub",
        description:
          "Sistem manajemen material untuk mengelola master barang, transaksi penerimaan dan pengeluaran, serta laporan stok yang lengkap.",
        about:
          "SigmaHub memusatkan pengelolaan material gudang. Sistem ini menyimpan master barang, mencatat transaksi masuk dan keluar, lalu menghasilkan laporan stok dan mutasi. Basis kode yang sama dirilis sebagai aplikasi web, build Android lewat Capacitor, dan build desktop lewat Electron.",
        keyFeatures: [
          "Master barang lengkap dengan kategori dan satuan",
          "Transaksi penerimaan dan pengeluaran barang",
          "Laporan mutasi dan saldo stok",
          "Akses berbasis peran dengan Supabase",
          "Build web, Android, dan desktop dari satu basis kode",
        ],
        challenges: [
          "Menjaga saldo stok tetap benar saat transaksi bersamaan",
          "Mengemas satu basis kode React untuk web, mobile, dan desktop",
          "Merancang laporan yang tetap terbaca di layar kecil",
        ],
      },
    },
  },
  {
    id: "invoiceku",
    link: "https://invoiceku.vercel.app",
    thumbnail: invoicekuShot,
    tags: ["Web", "Invoice", "PDF", "SaaS"],
    category: "Business Tool",
    role: "Full Stack Developer",
    technologies: ["Next.js", "React", "Supabase", "Resend", "html2pdf"],
    copy: {
      en: {
        title: "Faktura — Invoice Generator",
        description:
          "Invoice generator with seven business presets, live preview editing, PDF export, shareable links, saved invoices, and email delivery.",
        about:
          "Faktura turns invoicing into a single screen: pick a business preset, fill the form, and watch the invoice render live beside it. Invoices can be downloaded as PDF, shared through an encoded link without an account, or saved and managed after signing in, with delivery by email.",
        keyFeatures: [
          "Seven business presets with automatic colour themes",
          "Form editor with real-time invoice preview",
          "PDF download and print-ready layout",
          "Shareable link with invoice data encoded in the URL",
          "Account-based invoice storage and email sending",
        ],
        challenges: [
          "Producing a print-accurate PDF from an HTML preview",
          "Encoding a full invoice into a shareable URL safely",
          "Supporting both anonymous and signed-in workflows",
        ],
      },
      id: {
        title: "Faktura — Generator Invoice",
        description:
          "Generator invoice dengan tujuh preset bidang usaha, edit dengan preview langsung, unduh PDF, bagikan link, simpan invoice, dan kirim email.",
        about:
          "Faktura merangkum pembuatan invoice dalam satu layar: pilih preset bidang usaha, isi formulir, dan invoice langsung tampil di sebelahnya. Invoice bisa diunduh sebagai PDF, dibagikan lewat link terenkode tanpa akun, atau disimpan dan dikelola setelah login, lengkap dengan pengiriman email.",
        keyFeatures: [
          "Tujuh preset bidang usaha dengan tema warna otomatis",
          "Editor formulir dengan preview invoice real-time",
          "Unduh PDF dan tata letak siap cetak",
          "Bagikan link dengan data invoice terenkode di URL",
          "Penyimpanan invoice per akun dan pengiriman email",
        ],
        challenges: [
          "Menghasilkan PDF yang akurat dari preview HTML",
          "Mengenkode satu invoice penuh ke dalam URL secara aman",
          "Mendukung alur tanpa akun sekaligus alur setelah login",
        ],
      },
    },
  },
  {
    id: "flytcu",
    link: null,
    thumbnail: flytcuShot,
    tags: ["Web", "Booking", "Next.js", "Prisma"],
    category: "Booking Platform",
    role: "Full Stack Developer",
    technologies: ["Next.js", "TypeScript", "Prisma", "PostgreSQL", "Tailwind CSS"],
    copy: {
      en: {
        title: "FlyTcu",
        description:
          "Flight ticket booking platform with flight search, seat selection, checkout, flash sales, travel packages, and an admin dashboard.",
        about:
          "FlyTcu covers the full booking journey: browse and search available flights, choose a seat, check out, and retrieve tickets afterwards. Alongside the customer flow it ships an admin dashboard for managing flights, packages, and transactions, backed by Prisma and PostgreSQL.",
        keyFeatures: [
          "Flight search with availability and seat selection",
          "Checkout flow with booking confirmation and my-tickets page",
          "Flash sale and travel package sections",
          "Admin dashboard for flights and transactions",
          "Relational data model managed with Prisma",
        ],
        challenges: [
          "Preventing double-booked seats during checkout",
          "Modelling flights, seats, and transactions relationally",
          "Splitting customer and admin areas within one app router",
        ],
      },
      id: {
        title: "FlyTcu",
        description:
          "Platform pemesanan tiket pesawat dengan pencarian penerbangan, pilih kursi, checkout, flash sale, paket wisata, dan dashboard admin.",
        about:
          "FlyTcu mencakup seluruh alur pemesanan: menelusuri dan mencari penerbangan, memilih kursi, checkout, lalu melihat tiket yang sudah dibeli. Selain alur pelanggan, tersedia dashboard admin untuk mengelola penerbangan, paket, dan transaksi, ditopang Prisma dan PostgreSQL.",
        keyFeatures: [
          "Pencarian penerbangan dengan ketersediaan dan pilih kursi",
          "Alur checkout dengan konfirmasi dan halaman tiket saya",
          "Bagian flash sale dan paket perjalanan",
          "Dashboard admin untuk penerbangan dan transaksi",
          "Model data relasional yang dikelola dengan Prisma",
        ],
        challenges: [
          "Mencegah kursi terpesan ganda saat checkout",
          "Memodelkan penerbangan, kursi, dan transaksi secara relasional",
          "Memisahkan area pelanggan dan admin dalam satu app router",
        ],
      },
    },
  },
  {
    id: "splitbill",
    link: null,
    thumbnail: splitbillShot,
    tags: ["Web", "Mobile", "PWA", "FastAPI"],
    category: "Cross-platform Application",
    role: "Full Stack Developer",
    technologies: ["Next.js", "TypeScript", "Expo", "React Native", "FastAPI", "Supabase"],
    copy: {
      en: {
        title: "SplitBill",
        description:
          "Bill splitting app across web, Android, and iOS — a Next.js PWA and an Expo mobile client sharing a FastAPI backend.",
        about:
          "SplitBill splits shared bills between friends without spreadsheet maths. It is structured as a monorepo: a Next.js PWA for the web, an Expo React Native client covering Android and iOS from one codebase, and a FastAPI service with Supabase behind both, so one set of business rules serves every platform.",
        keyFeatures: [
          "Bill entry with per-person item allocation",
          "Next.js PWA installable on desktop and mobile browsers",
          "Expo React Native client for Android and iOS",
          "Shared FastAPI backend with Supabase storage",
          "Monorepo with shared packages between apps",
        ],
        challenges: [
          "Keeping split calculations identical across three clients",
          "Sharing types and logic through a monorepo workspace",
          "Choosing infrastructure that stays cheap at low traffic",
        ],
      },
      id: {
        title: "SplitBill",
        description:
          "Aplikasi bagi tagihan untuk web, Android, dan iOS — PWA Next.js dan klien mobile Expo yang berbagi backend FastAPI.",
        about:
          "SplitBill membagi tagihan bersama tanpa hitung-hitungan manual di spreadsheet. Strukturnya monorepo: PWA Next.js untuk web, klien Expo React Native untuk Android dan iOS dari satu basis kode, serta layanan FastAPI dengan Supabase di belakang keduanya, sehingga satu aturan bisnis melayani semua platform.",
        keyFeatures: [
          "Input tagihan dengan alokasi item per orang",
          "PWA Next.js yang bisa dipasang di desktop dan browser mobile",
          "Klien Expo React Native untuk Android dan iOS",
          "Backend FastAPI bersama dengan penyimpanan Supabase",
          "Monorepo dengan paket bersama antar aplikasi",
        ],
        challenges: [
          "Menjaga hasil perhitungan sama persis di tiga klien",
          "Berbagi tipe dan logika lewat workspace monorepo",
          "Memilih infrastruktur yang tetap murah saat trafik rendah",
        ],
      },
    },
  },
  {
    id: "slay-heroes",
    link: "https://slay-heroes.vercel.app",
    thumbnail: slayHeroesShot,
    tags: ["Game", "Web", "AI Opponents", "TypeScript"],
    category: "Browser Game",
    role: "Game Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase"],
    copy: {
      en: {
        title: "Slay Heroes",
        description:
          "Competitive fantasy card game in the browser — recruit six hero classes or slay three beasts before your rivals, against one to five AI bots.",
        about:
          "Slay Heroes is an original digital card game in the party-building and take-that genre. A match is won by recruiting six hero classes or slaying three beasts first, played solo against one to five AI opponents. All card names, art direction, and card text are original work.",
        keyFeatures: [
          "Two win conditions: recruit six heroes or slay three beasts",
          "One to five AI opponents with distinct play behaviour",
          "Full turn engine with card effects and combat resolution",
          "Original card set, art direction, and rules text",
          "Runs entirely in the browser, no install required",
        ],
        challenges: [
          "Writing AI opponents that play plausibly without cheating",
          "Resolving card effects deterministically across turns",
          "Balancing two competing win conditions",
        ],
      },
      id: {
        title: "Slay Heroes",
        description:
          "Game kartu fantasi kompetitif di browser — rekrut enam kelas hero atau kalahkan tiga beast sebelum lawan, melawan satu sampai lima bot AI.",
        about:
          "Slay Heroes adalah game kartu digital orisinal bergenre party-building dan take-that. Satu pertandingan dimenangkan dengan merekrut enam kelas hero atau mengalahkan tiga beast lebih dulu, dimainkan solo melawan satu sampai lima lawan AI. Semua nama kartu, arah visual, dan teks kartu dibuat sendiri.",
        keyFeatures: [
          "Dua kondisi menang: rekrut enam hero atau kalahkan tiga beast",
          "Satu sampai lima lawan AI dengan perilaku bermain berbeda",
          "Mesin giliran lengkap dengan efek kartu dan resolusi pertarungan",
          "Set kartu, arah visual, dan teks aturan orisinal",
          "Berjalan penuh di browser tanpa perlu instalasi",
        ],
        challenges: [
          "Membuat lawan AI bermain masuk akal tanpa curang",
          "Menyelesaikan efek kartu secara deterministik antar giliran",
          "Menyeimbangkan dua kondisi menang yang bersaing",
        ],
      },
    },
  },
  {
    id: "tcuber-academy",
    link: "https://tcuberacademy.vercel.app",
    thumbnail: tcuberAcademyShot,
    tags: ["Web", "Education", "Landing page", "UI"],
    category: "Education Platform",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS", "Supabase"],
    copy: {
      en: {
        title: "Tcuber Academy",
        description:
          "Web development class platform offering online and onsite tracks — project-based curriculum, mentoring, and an active community.",
        about:
          "Tcuber Academy presents a web development programme that takes students from zero to job-ready. The site lays out both the online and onsite tracks, the project-based curriculum, mentoring model, and community, and handles enquiries and registration.",
        keyFeatures: [
          "Separate online and onsite class tracks",
          "Project-based curriculum breakdown per module",
          "Mentor and community sections",
          "Registration and enquiry flow",
          "Responsive marketing pages with clear pricing",
        ],
        challenges: [
          "Presenting two class formats without confusing visitors",
          "Structuring curriculum content so it stays easy to update",
          "Keeping page weight low with image-heavy sections",
        ],
      },
      id: {
        title: "Tcuber Academy",
        description:
          "Platform kelas web development dengan jalur online dan onsite — kurikulum berbasis proyek, mentor, dan komunitas aktif.",
        about:
          "Tcuber Academy menyajikan program web development dari nol sampai siap kerja. Situsnya memaparkan jalur online dan onsite, kurikulum berbasis proyek, model mentoring, dan komunitas, sekaligus menangani pertanyaan dan pendaftaran.",
        keyFeatures: [
          "Jalur kelas online dan onsite yang terpisah",
          "Rincian kurikulum berbasis proyek per modul",
          "Bagian mentor dan komunitas",
          "Alur pendaftaran dan tanya jawab",
          "Halaman responsif dengan harga yang jelas",
        ],
        challenges: [
          "Menyajikan dua format kelas tanpa membingungkan pengunjung",
          "Menyusun konten kurikulum agar mudah diperbarui",
          "Menjaga bobot halaman tetap ringan di bagian yang penuh gambar",
        ],
      },
    },
  },
  {
    id: "paumedika",
    link: "https://paumedika.vercel.app",
    thumbnail: paumedikaShot,
    tags: ["Web", "Company profile", "Healthcare", "UI"],
    category: "Company Profile",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS"],
    copy: {
      en: {
        title: "PAU — Putri Arfra Utama",
        description:
          "Company profile for an Indonesian medical supply distributor: antiseptics, masks, disinfectants, PPE, and other medical equipment.",
        about:
          "PAU (Putri Arfra Utama) is an official medical supply distributor in Indonesia. The site presents the product range — antiseptics, masks, disinfectants, and personal protective equipment — alongside company credentials and a direct enquiry channel for buyers.",
        keyFeatures: [
          "Product catalogue grouped by category",
          "Company credentials and distribution coverage",
          "Direct enquiry and WhatsApp contact",
          "SEO metadata tuned for medical supply searches",
          "Responsive layout for mobile-first buyers",
        ],
        challenges: [
          "Presenting a wide product range without overwhelming pages",
          "Making enquiry paths obvious on mobile",
          "Keeping product content editable by non-developers",
        ],
      },
      id: {
        title: "PAU — Putri Arfra Utama",
        description:
          "Company profile distributor alat kesehatan resmi di Indonesia: antiseptik, masker, disinfektan, APD, dan alkes lainnya.",
        about:
          "PAU (Putri Arfra Utama) adalah distributor alat kesehatan resmi di Indonesia. Situsnya menampilkan rentang produk — antiseptik, masker, disinfektan, dan alat pelindung diri — beserta kredensial perusahaan dan jalur pemesanan langsung untuk pembeli.",
        keyFeatures: [
          "Katalog produk yang dikelompokkan per kategori",
          "Kredensial perusahaan dan cakupan distribusi",
          "Kontak pemesanan langsung dan WhatsApp",
          "Metadata SEO untuk pencarian alat kesehatan",
          "Tata letak responsif untuk pembeli via mobile",
        ],
        challenges: [
          "Menyajikan banyak produk tanpa membuat halaman sesak",
          "Membuat jalur pemesanan jelas di layar mobile",
          "Menjaga konten produk mudah diubah oleh non-developer",
        ],
      },
    },
  },
  {
    id: "sales-tracker",
    link: "https://shampootracker.vercel.app",
    thumbnail: salesTrackerShot,
    tags: ["Web", "Mobile", "Sales", "Android"],
    category: "Sales Tracking App",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "Capacitor"],
    copy: {
      en: {
        title: "Sales Tracker",
        description:
          "Sales tracking app for distributors — record orders in the field, monitor targets, and review performance on web or Android.",
        about:
          "Sales Tracker gives distributor sales teams a fast way to record what they sell in the field. Orders and outlet visits are logged on a phone, synced to Supabase, and rolled up into performance views. The web build is wrapped with Capacitor to ship as an Android app from the same source.",
        keyFeatures: [
          "Field order entry designed for phone use",
          "Outlet and customer records",
          "Target versus realisation performance views",
          "Supabase sync with offline-tolerant entry",
          "Android build packaged via Capacitor",
        ],
        challenges: [
          "Making data entry fast enough for use during visits",
          "Handling unreliable connectivity in the field",
          "Sharing one codebase between web and Android builds",
        ],
      },
      id: {
        title: "Sales Tracker",
        description:
          "Aplikasi tracking penjualan untuk distributor — catat order di lapangan, pantau target, dan lihat performa lewat web atau Android.",
        about:
          "Sales Tracker memberi tim penjualan distributor cara cepat mencatat penjualan di lapangan. Order dan kunjungan outlet dicatat dari ponsel, disinkronkan ke Supabase, lalu diringkas menjadi tampilan performa. Build web dibungkus Capacitor sehingga dirilis juga sebagai aplikasi Android dari sumber yang sama.",
        keyFeatures: [
          "Input order lapangan yang dirancang untuk ponsel",
          "Data outlet dan pelanggan",
          "Tampilan performa target versus realisasi",
          "Sinkronisasi Supabase dengan input yang toleran offline",
          "Build Android lewat Capacitor",
        ],
        challenges: [
          "Membuat input data cukup cepat dipakai saat kunjungan",
          "Menangani koneksi yang tidak stabil di lapangan",
          "Memakai satu basis kode untuk build web dan Android",
        ],
      },
    },
  },
  {
    id: "liveprod-cp",
    link: "https://liveprodcp.vercel.app",
    thumbnail: liveprodCpShot,
    tags: ["Web", "Event", "Community", "Supabase"],
    category: "Event Portal",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS", "Supabase"],
    copy: {
      en: {
        title: "Live Production — GL Central Park 2026",
        description:
          "Official portal for the GL Central Park 2026 multimedia gathering: participant registration, merchandise pre-orders, rundown, and crew information.",
        about:
          "The portal is the single reference for everyone involved in the GL Central Park 2026 multimedia live production gathering. Participants register and pre-order shirts, while committee and multimedia crew read the rundown and event details from the same place instead of chasing chat messages.",
        keyFeatures: [
          "Participant registration list",
          "Shirt pre-order with size selection",
          "Event rundown and schedule",
          "Committee and multimedia crew information",
          "Supabase-backed data with admin access",
        ],
        challenges: [
          "Handling registration and pre-order data in one flow",
          "Keeping the rundown readable on phones during the event",
          "Restricting admin actions to committee members",
        ],
      },
      id: {
        title: "Live Production — GL Central Park 2026",
        description:
          "Portal resmi Gathering Multimedia GL Central Park 2026: pendaftaran peserta, pre-order kaos, rundown acara, dan info panitia.",
        about:
          "Portal ini jadi rujukan tunggal untuk semua yang terlibat di Gathering Multimedia Live Production GL Central Park 2026. Peserta mendaftar dan pre-order kaos, sementara panitia serta tim multimedia membaca rundown dan info acara dari tempat yang sama, bukan dari percakapan chat yang tercecer.",
        keyFeatures: [
          "Daftar pendaftaran peserta",
          "Pre-order kaos dengan pilihan ukuran",
          "Rundown dan jadwal acara",
          "Informasi panitia dan tim multimedia",
          "Data berbasis Supabase dengan akses admin",
        ],
        challenges: [
          "Menangani data pendaftaran dan pre-order dalam satu alur",
          "Menjaga rundown tetap terbaca di ponsel saat acara",
          "Membatasi aksi admin hanya untuk panitia",
        ],
      },
    },
  },
  {
    id: "cg-schedule-pro",
    link: null,
    thumbnail: cgScheduleProShot,
    tags: ["Web", "Scheduling", "TanStack Start", "Supabase"],
    category: "Scheduling System",
    role: "Full Stack Developer",
    technologies: ["TanStack Start", "React", "TypeScript", "Supabase", "Tailwind CSS"],
    copy: {
      en: {
        title: "CG Schedule Pro",
        description:
          "Volunteer scheduling system with teams, positions, sessions, a schedule builder, per-member views, and backup and restore.",
        about:
          "CG Schedule Pro replaces the spreadsheet used to roster volunteers across services. Coordinators define teams, positions, and sessions, then assemble rosters in a schedule builder; each member sees only their own assignments. Backup and restore keeps a season of schedules recoverable.",
        keyFeatures: [
          "Team, position, and session master data",
          "Schedule builder with assignment per position",
          "Personal member schedule view",
          "User management with role-based access",
          "Backup and restore of schedule data",
        ],
        challenges: [
          "Preventing double-booking a member across positions",
          "Making the builder usable for non-technical coordinators",
          "Restoring backups without corrupting existing rosters",
        ],
      },
      id: {
        title: "CG Schedule Pro",
        description:
          "Sistem penjadwalan pelayan dengan tim, posisi, sesi, schedule builder, tampilan per anggota, serta backup dan restore.",
        about:
          "CG Schedule Pro menggantikan spreadsheet yang dipakai menyusun jadwal pelayan lintas ibadah. Koordinator menentukan tim, posisi, dan sesi, lalu menyusun jadwal lewat schedule builder; tiap anggota hanya melihat tugasnya sendiri. Backup dan restore membuat jadwal satu musim tetap bisa dipulihkan.",
        keyFeatures: [
          "Master data tim, posisi, dan sesi",
          "Schedule builder dengan penugasan per posisi",
          "Tampilan jadwal pribadi tiap anggota",
          "Manajemen pengguna dengan akses berbasis peran",
          "Backup dan restore data jadwal",
        ],
        challenges: [
          "Mencegah satu anggota terjadwal ganda di beberapa posisi",
          "Membuat builder nyaman dipakai koordinator non-teknis",
          "Memulihkan backup tanpa merusak jadwal yang sudah ada",
        ],
      },
    },
  },
  {
    id: "dokum-gsc-2026",
    link: "https://dokum-gsc2026.vercel.app",
    thumbnail: dokumGsc2026Shot,
    tags: ["Web", "Scheduling", "Documentation", "Supabase"],
    category: "Scheduling System",
    role: "Full Stack Developer",
    technologies: ["TanStack Start", "React", "TypeScript", "Supabase", "Tailwind CSS"],
    copy: {
      en: {
        title: "Dokum GSC 2026",
        description:
          "Scheduling system for the GSC 2026 documentation team — sessions, positions, member rosters, and personal schedule views.",
        about:
          "Dokum GSC 2026 keeps the documentation crew organised across the event season. Coordinators register members, define positions and sessions, and publish rosters; crew members open a personal view showing where and when they serve, with data backed up and restorable.",
        keyFeatures: [
          "Member registry for the documentation team",
          "Position and session configuration",
          "Roster publishing with per-member schedule view",
          "Authentication with role-based permissions",
          "Backup and restore of scheduling data",
        ],
        challenges: [
          "Adapting a generic scheduling model to documentation roles",
          "Keeping personal schedules accurate after roster edits",
          "Serving fast on venue connections during events",
        ],
      },
      id: {
        title: "Dokum GSC 2026",
        description:
          "Sistem penjadwalan tim dokumentasi GSC 2026 — sesi, posisi, daftar anggota, dan tampilan jadwal pribadi.",
        about:
          "Dokum GSC 2026 merapikan koordinasi tim dokumentasi sepanjang musim acara. Koordinator mendaftarkan anggota, menentukan posisi dan sesi, lalu menerbitkan jadwal; anggota membuka tampilan pribadi berisi kapan dan di mana mereka bertugas, dengan data yang bisa dibackup dan dipulihkan.",
        keyFeatures: [
          "Registri anggota tim dokumentasi",
          "Pengaturan posisi dan sesi",
          "Penerbitan jadwal dengan tampilan per anggota",
          "Autentikasi dengan izin berbasis peran",
          "Backup dan restore data penjadwalan",
        ],
        challenges: [
          "Menyesuaikan model penjadwalan umum ke peran dokumentasi",
          "Menjaga jadwal pribadi tetap akurat setelah jadwal diubah",
          "Tetap cepat diakses lewat koneksi venue saat acara",
        ],
      },
    },
  },
  {
    id: "sendy-tg-job-center",
    link: "https://sendytgjobcenter.vercel.app",
    thumbnail: sendyTgJobCenterShot,
    tags: ["Web", "Landing page", "Recruitment", "TanStack Start"],
    category: "Landing Page",
    role: "Full Stack Developer",
    technologies: ["TanStack Start", "React", "TypeScript", "Tailwind CSS"],
    copy: {
      en: {
        title: "Sendy TG Job Center",
        description:
          "Landing page for an external agent placing Indonesian workers into Japan's Tokutei Ginou (SSW) programme.",
        about:
          "The site introduces an external agent for Japan's Tokutei Ginou (Specified Skilled Worker) programme. It explains the placement process, eligible job sectors, and requirements, then routes interested candidates into a direct enquiry channel.",
        keyFeatures: [
          "Programme explanation for Tokutei Ginou / SSW",
          "Eligible sectors and requirement breakdown",
          "Step-by-step placement process",
          "Direct enquiry and messaging contact",
          "Fast, responsive single-page layout",
        ],
        challenges: [
          "Explaining an unfamiliar visa programme in plain language",
          "Keeping the candidate enquiry path short",
          "Serving a mobile-first audience on modest connections",
        ],
      },
      id: {
        title: "Sendy TG Job Center",
        description:
          "Landing page agen eksternal penempatan pekerja Indonesia ke program Tokutei Ginou (SSW) Jepang.",
        about:
          "Situs ini memperkenalkan agen eksternal untuk program Tokutei Ginou (Specified Skilled Worker) Jepang. Isinya menjelaskan proses penempatan, sektor pekerjaan yang tersedia, dan persyaratannya, lalu mengarahkan kandidat ke jalur tanya jawab langsung.",
        keyFeatures: [
          "Penjelasan program Tokutei Ginou / SSW",
          "Rincian sektor dan persyaratan",
          "Proses penempatan langkah demi langkah",
          "Kontak tanya jawab langsung",
          "Tata letak satu halaman yang cepat dan responsif",
        ],
        challenges: [
          "Menjelaskan program visa yang asing dengan bahasa sederhana",
          "Menjaga jalur tanya jawab kandidat tetap singkat",
          "Melayani audiens mobile dengan koneksi terbatas",
        ],
      },
    },
  },
  {
    id: "ayam-juara",
    link: null,
    thumbnail: ayamJuaraShot,
    tags: ["Web", "Concept redesign", "F&B", "Unaffiliated"],
    category: "Concept Redesign (Unaffiliated)",
    role: "Frontend Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS"],
    copy: {
      en: {
        title: "KFC — Concept Redesign",
        description:
          "An independent, unaffiliated concept redesign of a fried chicken chain's site: menu, deals, brand story, and store locator. Not client work and not endorsed by the brand.",
        about:
          "A self-directed design exercise, not a commission. The brief was to take a fast-food site — a category that usually buries the menu under promotional clutter — and rebuild it so the food leads and the ordering path stays short. The result runs menu categories, a deals section, a brand story, and a store locator across one page. All names, marks, and slogans shown belong to their owner and appear here only as study material; the project has no affiliation with, sponsorship by, or endorsement from the brand.",
        keyFeatures: [
          "Full-bleed hero with the product carrying the page",
          "Menu split by category: chicken, burgers, desserts, drinks",
          "Deals section for bundles and limited-time items",
          "Brand story and store locator sections",
          "Mobile-first layout kept light despite heavy imagery",
        ],
        challenges: [
          "Holding legibility over full-bleed food photography",
          "Keeping an image-led page fast on mobile data",
          "Structuring a large menu so it stays scannable at a glance",
        ],
      },
      id: {
        title: "KFC — Konsep Redesign",
        description:
          "Redesign konsep mandiri untuk situs gerai ayam goreng: menu, promo, cerita brand, dan pencari gerai. Bukan proyek klien dan tidak berafiliasi dengan brand-nya.",
        about:
          "Latihan desain mandiri, bukan pekerjaan pesanan. Tantangannya: mengambil situs fast food — kategori yang biasanya mengubur menu di balik tumpukan promo — lalu menyusunnya ulang agar makanannya yang memimpin dan jalur pemesanan tetap pendek. Hasilnya memuat kategori menu, bagian promo, cerita brand, dan pencari gerai dalam satu halaman. Seluruh nama, logo, dan slogan yang tampil adalah milik pemiliknya dan dipakai di sini semata sebagai bahan studi; proyek ini tidak berafiliasi, tidak disponsori, dan tidak didukung oleh brand tersebut.",
        keyFeatures: [
          "Hero full-bleed dengan produk sebagai pemimpin halaman",
          "Menu terbagi per kategori: ayam, burger, pencuci mulut, minuman",
          "Bagian promo untuk paket bundling dan item terbatas",
          "Bagian cerita brand dan pencari gerai",
          "Tata letak mobile-first yang tetap ringan meski padat gambar",
        ],
        challenges: [
          "Menjaga keterbacaan teks di atas foto makanan full-bleed",
          "Membuat halaman padat gambar tetap cepat di kuota mobile",
          "Menyusun menu besar agar tetap mudah dipindai sekilas",
        ],
      },
    },
  },
  {
    id: "cg-ya124",
    link: "https://loveprint.vercel.app",
    thumbnail: cgYa124Shot,
    tags: ["Web", "Community", "Social", "Supabase"],
    category: "Community Platform",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS", "Supabase"],
    copy: {
      en: {
        title: "Connect Group YA124",
        description:
          "Mini social platform for a young adult faith community: activity posts, meeting attendance, and member connection.",
        about:
          "Connect Group YA124 is a small private social space for a young adult community group. Members post activities, record attendance at meetings, and stay connected between gatherings, with content scoped to the group rather than a public feed.",
        keyFeatures: [
          "Activity feed with member posts",
          "Meeting attendance recording",
          "Member directory within the group",
          "Authentication limited to group members",
          "Mobile-first layout for use during meetings",
        ],
        challenges: [
          "Keeping a social feed private to one group",
          "Making attendance quick to record on a phone",
          "Balancing moderation needs with a small, trusted group",
        ],
      },
      id: {
        title: "Connect Group YA124",
        description:
          "Platform sosial mini untuk komunitas rohani young adult: unggah aktivitas, absen pertemuan, dan saling terhubung.",
        about:
          "Connect Group YA124 adalah ruang sosial kecil dan tertutup untuk satu komunitas young adult. Anggota mengunggah aktivitas, mencatat kehadiran di pertemuan, dan tetap terhubung di antara jadwal berkumpul, dengan konten yang terbatas pada grup, bukan feed publik.",
        keyFeatures: [
          "Feed aktivitas berisi unggahan anggota",
          "Pencatatan kehadiran pertemuan",
          "Direktori anggota dalam grup",
          "Autentikasi khusus anggota grup",
          "Tata letak mobile-first untuk dipakai saat pertemuan",
        ],
        challenges: [
          "Menjaga feed sosial tetap privat untuk satu grup",
          "Membuat absensi cepat dicatat lewat ponsel",
          "Menyeimbangkan kebutuhan moderasi di grup kecil",
        ],
      },
    },
  },
  {
    id: "monica-house",
    link: "https://monica-house-system.vercel.app",
    thumbnail: monicaHouseShot,
    tags: ["Web", "Construction", "Reporting", "Python"],
    category: "Project Monitoring System",
    role: "Full Stack Developer",
    technologies: ["Python", "Vanilla JS", "Excel import", "Vercel"],
    copy: {
      en: {
        title: "MONICA HOUSE Project Monitoring",
        description:
          "Construction monitoring web app for RAB/BOQ tracking, physical progress, and cost realisation, seeded from project Excel files.",
        about:
          "Built for the MONICA HOUSE construction project, the app tracks the bill of quantities against physical progress and realised cost. Budget and unit-price data are imported directly from the project's Excel workbooks, and the backend runs on Python's standard library alone, so it deploys with no dependency chain.",
        keyFeatures: [
          "RAB/BOQ line items with unit price analysis",
          "Physical progress tracking per work item",
          "Cost realisation against budget",
          "Excel import of budget and unit-price data",
          "Zero-dependency Python backend",
        ],
        challenges: [
          "Importing inconsistent spreadsheet structures reliably",
          "Serving the app without external Python packages",
          "Presenting budget versus realisation clearly for site use",
        ],
      },
      id: {
        title: "Monitoring Proyek MONICA HOUSE",
        description:
          "Web app monitoring konstruksi untuk RAB/BOQ, progres fisik, dan realisasi biaya, dengan data dari file Excel proyek.",
        about:
          "Dibuat untuk proyek konstruksi MONICA HOUSE, aplikasi ini memantau bill of quantity terhadap progres fisik dan realisasi biaya. Data anggaran dan analisa harga satuan diimpor langsung dari workbook Excel proyek, sementara backend-nya hanya memakai pustaka bawaan Python sehingga bisa dirilis tanpa rantai dependensi.",
        keyFeatures: [
          "Item RAB/BOQ dengan analisa harga satuan",
          "Pelacakan progres fisik per item pekerjaan",
          "Realisasi biaya terhadap anggaran",
          "Impor data anggaran dan harga satuan dari Excel",
          "Backend Python tanpa dependensi eksternal",
        ],
        challenges: [
          "Mengimpor struktur spreadsheet yang tidak seragam secara andal",
          "Menjalankan aplikasi tanpa paket Python tambahan",
          "Menyajikan anggaran versus realisasi agar jelas dipakai di lapangan",
        ],
      },
    },
  },
  {
    id: "pelatihan-vba-sql-powerbi",
    link: null,
    thumbnail: pelatihanVbaShot,
    tags: ["Training", "VBA", "SQL", "Power BI"],
    category: "Training Programme",
    role: "Instructor & Curriculum Author",
    technologies: ["Excel VBA", "SQL", "Power BI", "Markdown"],
    copy: {
      en: {
        title: "VBA, SQL & Power BI Training",
        description:
          "Corporate training programme with per-session materials split into participant and instructor tracks, from Excel automation to reporting.",
        about:
          "A structured training programme covering Excel VBA automation, SQL querying, and Power BI reporting. Each session ships two versions of the material — a participant workbook and an instructor guide — authored in Markdown and rendered to distributable documents, so the same source drives both the class and the handouts.",
        keyFeatures: [
          "Session-by-session curriculum across VBA, SQL, and Power BI",
          "Separate participant and instructor material tracks",
          "Markdown source rendered to distributable documents",
          "Hands-on exercises tied to real reporting tasks",
          "Reusable structure for repeat delivery",
        ],
        challenges: [
          "Keeping participant and instructor versions in sync",
          "Pitching three different tools at one consistent level",
          "Producing clean documents from a Markdown source",
        ],
      },
      id: {
        title: "Pelatihan VBA, SQL & Power BI",
        description:
          "Program pelatihan korporat dengan materi per pertemuan yang dipisah untuk peserta dan pengajar, dari otomasi Excel sampai pelaporan.",
        about:
          "Program pelatihan terstruktur yang mencakup otomasi Excel VBA, kueri SQL, dan pelaporan Power BI. Tiap pertemuan punya dua versi materi — buku peserta dan panduan pengajar — ditulis dalam Markdown lalu dirender menjadi dokumen siap bagi, sehingga satu sumber melayani kelas sekaligus handout.",
        keyFeatures: [
          "Kurikulum per pertemuan untuk VBA, SQL, dan Power BI",
          "Jalur materi peserta dan pengajar yang terpisah",
          "Sumber Markdown yang dirender jadi dokumen siap bagi",
          "Latihan praktik yang terkait tugas pelaporan nyata",
          "Struktur yang bisa dipakai ulang untuk batch berikutnya",
        ],
        challenges: [
          "Menjaga versi peserta dan pengajar tetap sinkron",
          "Menyamakan level tiga alat yang berbeda",
          "Menghasilkan dokumen rapi dari sumber Markdown",
        ],
      },
    },
  },
  {
    id: "tsecure",
    link: "https://tsecure.vercel.app",
    thumbnail: tsecureShot,
    tags: ["Web", "Cloud Storage", "File Sharing", "Supabase"],
    category: "Cloud Storage / SaaS",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "PostgreSQL", "Tailwind CSS"],
    copy: {
      en: {
        title: "Tsecure",
        description:
          "Privacy-first cloud storage: folder browsing, file previews, expiring share links, trash with scheduled cleanup, and session management.",
        about:
          "Tsecure is a file workspace built around ownership and revocation. Files live in a folder tree the user browses with breadcrumbs, preview happens in place, and anything shared goes out as a link record that can be listed and withdrawn later. Deleted files land in a trash tier that a scheduled function reaps, and every active login shows up as a session the account owner can review. Row-level security and a role table keep the admin surface separate from ordinary accounts.",
        keyFeatures: [
          "Folder tree with breadcrumb navigation and grid file browser",
          "In-place file preview without leaving the workspace",
          "Shareable links backed by a link table, listable and revocable",
          "Trash tier reaped by a scheduled cleanup function",
          "Active session list and role-gated admin user management",
        ],
        challenges: [
          "Writing row-level security so shared links read files their recipient does not own",
          "Keeping trash cleanup safe against files restored mid-run",
          "Separating admin capability from user data access without a second backend",
        ],
      },
      id: {
        title: "Tsecure",
        description:
          "Penyimpanan cloud yang mengutamakan privasi: telusur folder, pratinjau berkas, tautan berbagi yang bisa dicabut, tong sampah dengan pembersihan terjadwal, dan manajemen sesi.",
        about:
          "Tsecure adalah ruang kerja berkas yang dirancang di sekitar kepemilikan dan pencabutan akses. Berkas tersimpan dalam struktur folder yang ditelusuri lewat breadcrumb, pratinjau berjalan langsung di tempat, dan setiap berbagi tercatat sebagai tautan yang bisa didaftar lalu dicabut. Berkas terhapus masuk ke tong sampah yang dibersihkan fungsi terjadwal, dan setiap login aktif tampil sebagai sesi yang bisa ditinjau pemilik akun. Row-level security dan tabel peran memisahkan area admin dari akun biasa.",
        keyFeatures: [
          "Struktur folder dengan navigasi breadcrumb dan tampilan grid berkas",
          "Pratinjau berkas langsung tanpa keluar dari ruang kerja",
          "Tautan berbagi bertumpu pada tabel tautan, bisa didaftar dan dicabut",
          "Tong sampah yang dibersihkan fungsi terjadwal",
          "Daftar sesi aktif dan manajemen pengguna admin berbasis peran",
        ],
        challenges: [
          "Menyusun row-level security agar tautan berbagi bisa membaca berkas milik orang lain",
          "Menjaga pembersihan tong sampah tetap aman saat ada berkas dipulihkan di tengah proses",
          "Memisahkan wewenang admin dari akses data pengguna tanpa backend kedua",
        ],
      },
    },
  },
  {
    id: "tcu-analytics",
    link: "https://tcutrade.lovable.app",
    thumbnail: tcuAnalyticsShot,
    tags: ["Web", "Fintech", "Market Data", "Charts"],
    category: "Analytics Dashboard",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase Edge Functions", "Recharts", "Tailwind CSS"],
    copy: {
      en: {
        title: "Tcu Analytics",
        description:
          "Market dashboard pulling stocks, crypto, and currencies into one view, with portfolio tracking and performance breakdowns.",
        about:
          "Tcu Analytics collects quotes from several public market sources and presents them as one dashboard instead of several tabs. Equity and index data come from Yahoo Finance, crypto from CoinGecko, each behind an edge function so API access stays off the client. On top of the market pages sit a portfolio view and a performance section that chart holdings over time with Recharts.",
        keyFeatures: [
          "Stocks, crypto, currencies, and global market pages in one dashboard",
          "Quotes proxied through edge functions instead of direct client calls",
          "Portfolio tracking with performance and analysis views",
          "Time-series charts rendered with Recharts",
          "Authenticated accounts with notifications and settings",
        ],
        challenges: [
          "Normalising quote payloads from two providers with different shapes",
          "Keeping API credentials server-side while the UI stays fully client-rendered",
          "Rendering many simultaneous charts without stalling the page",
        ],
      },
      id: {
        title: "Tcu Analytics",
        description:
          "Dashboard pasar yang menyatukan saham, kripto, dan mata uang dalam satu tampilan, lengkap dengan pelacakan portofolio dan rincian performa.",
        about:
          "Tcu Analytics mengumpulkan kuotasi dari beberapa sumber pasar publik dan menyajikannya sebagai satu dashboard, bukan beberapa tab terpisah. Data saham dan indeks diambil dari Yahoo Finance, kripto dari CoinGecko, keduanya lewat edge function agar akses API tidak terekspos di sisi klien. Di atas halaman pasar terdapat tampilan portofolio dan bagian performa yang menggambar grafik kepemilikan dari waktu ke waktu memakai Recharts.",
        keyFeatures: [
          "Halaman saham, kripto, mata uang, dan pasar global dalam satu dashboard",
          "Kuotasi disalurkan lewat edge function, bukan panggilan langsung dari klien",
          "Pelacakan portofolio dengan tampilan performa dan analisis",
          "Grafik deret waktu digambar dengan Recharts",
          "Akun terautentikasi lengkap dengan notifikasi dan pengaturan",
        ],
        challenges: [
          "Menyeragamkan bentuk data kuotasi dari dua penyedia yang formatnya berbeda",
          "Menjaga kredensial API tetap di sisi server meski UI sepenuhnya dirender di klien",
          "Menggambar banyak grafik sekaligus tanpa membuat halaman tersendat",
        ],
      },
    },
  },
  {
    id: "tcustream",
    link: "https://tcustream.lovable.app",
    thumbnail: tcustreamShot,
    tags: ["Web", "Streaming", "TMDB", "Supabase"],
    category: "Media Platform",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "TMDB API", "Tailwind CSS"],
    copy: {
      en: {
        title: "Tcustream",
        description:
          "Film and series catalogue built on TMDB, with hover trailer previews, genre browsing, personal watchlists, and a full set of support pages.",
        about:
          "Tcustream is a streaming-style catalogue front end backed by the TMDB API through an edge function. Titles surface in carousels that play a trailer on hover, detail pages cover both films and series, and signed-in visitors keep a personal watchlist. Around the catalogue sits the supporting scaffolding a real product needs — help, FAQ, blog, careers, press, privacy, and terms — so the site reads as a complete service rather than a demo.",
        keyFeatures: [
          "Movie and series catalogue sourced from TMDB via an edge function",
          "Carousels with hover-triggered trailer playback",
          "Genre browsing plus separate film and series detail pages",
          "Personal watchlist tied to an authenticated profile",
          "Complete support surface: help, FAQ, blog, careers, press, legal",
        ],
        challenges: [
          "Keeping the TMDB key server-side while the catalogue renders client-side",
          "Preventing hover trailers from firing on every accidental pointer pass",
          "Holding layout stable while carousel artwork streams in at different rates",
        ],
      },
      id: {
        title: "Tcustream",
        description:
          "Katalog film dan serial berbasis TMDB, dengan pratinjau trailer saat hover, penelusuran genre, watchlist pribadi, dan halaman pendukung lengkap.",
        about:
          "Tcustream adalah front end katalog bergaya layanan streaming yang datanya diambil dari TMDB API lewat edge function. Judul tampil dalam carousel yang memutar trailer saat kursor melintas, halaman detail tersedia untuk film maupun serial, dan pengunjung yang masuk punya watchlist pribadi. Di sekeliling katalog tersedia kelengkapan yang dimiliki produk sungguhan — bantuan, FAQ, blog, karier, pers, privasi, dan syarat — sehingga situs terbaca sebagai layanan utuh, bukan demo.",
        keyFeatures: [
          "Katalog film dan serial bersumber dari TMDB lewat edge function",
          "Carousel dengan pemutaran trailer saat hover",
          "Penelusuran genre serta halaman detail terpisah untuk film dan serial",
          "Watchlist pribadi yang terikat pada profil terautentikasi",
          "Halaman pendukung lengkap: bantuan, FAQ, blog, karier, pers, legal",
        ],
        challenges: [
          "Menjaga kunci TMDB tetap di sisi server sementara katalog dirender di klien",
          "Mencegah trailer hover terpicu setiap kali kursor lewat tanpa sengaja",
          "Menjaga tata letak tetap stabil saat gambar carousel masuk pada laju berbeda",
        ],
      },
    },
  },
  {
    id: "tcu-project",
    link: "https://tcuproject.lovable.app",
    thumbnail: tcuProjectShot,
    tags: ["Web", "Project Management", "Admin panel", "Supabase"],
    category: "Business System",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "PostgreSQL", "Tailwind CSS"],
    copy: {
      en: {
        title: "TCu Project",
        description:
          "Project and payment tracker with bulk import, per-project todos, user and email management, and a scheduled daily reset.",
        about:
          "TCu Project is an internal operations panel: projects, their todo lists, and the payments attached to them, with a dashboard summarising the state. It leans on bulk import so records can arrive by spreadsheet rather than one form at a time, and a scheduled function performs a daily reset with its own audit log. Admin capabilities — listing users, deleting accounts, managing a master email list — sit behind a role table rather than a separate service.",
        keyFeatures: [
          "Project register with per-project todo lists",
          "Payment tracking tied to individual projects",
          "Bulk import dialog for spreadsheet-scale data entry",
          "Scheduled daily reset with its own audit log table",
          "Role-gated user, account, and master email management",
        ],
        challenges: [
          "Validating bulk imports without rejecting an entire batch for one bad row",
          "Making the daily reset idempotent so a retry cannot double-apply",
          "Keeping large sortable tables responsive as the register grows",
        ],
      },
      id: {
        title: "TCu Project",
        description:
          "Pelacak proyek dan pembayaran dengan impor massal, todo per proyek, pengelolaan pengguna dan email, serta reset harian terjadwal.",
        about:
          "TCu Project adalah panel operasional internal: proyek, daftar todo di dalamnya, dan pembayaran yang menempel padanya, dengan dashboard yang merangkum keadaan. Sistem ini bertumpu pada impor massal agar data bisa masuk lewat spreadsheet, bukan satu formulir per catatan, dan sebuah fungsi terjadwal menjalankan reset harian dengan log audit tersendiri. Wewenang admin — mendaftar pengguna, menghapus akun, mengelola daftar email induk — berada di balik tabel peran, bukan layanan terpisah.",
        keyFeatures: [
          "Daftar proyek dengan todo untuk tiap proyek",
          "Pelacakan pembayaran yang terikat pada masing-masing proyek",
          "Dialog impor massal untuk entri data berskala spreadsheet",
          "Reset harian terjadwal dengan tabel log audit sendiri",
          "Pengelolaan pengguna, akun, dan email induk berbasis peran",
        ],
        challenges: [
          "Memvalidasi impor massal tanpa menolak seluruh batch hanya karena satu baris rusak",
          "Membuat reset harian idempoten agar pengulangan tidak menerapkannya dua kali",
          "Menjaga tabel besar yang bisa disortir tetap responsif seiring data bertambah",
        ],
      },
    },
  },
  {
    id: "cloud-career-accelerator",
    link: "https://cloudcareer.lovable.app",
    thumbnail: cloudCareerShot,
    tags: ["Web", "AI", "EdTech", "Stripe"],
    category: "AI / Education Platform",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase Edge Functions", "Gemini", "Stripe", "Tailwind CSS"],
    copy: {
      en: {
        title: "Cloud Career Accelerator",
        description:
          "AI career coach that turns a skills assessment into a generated study plan, weekly roadmap, and learning material, behind a subscription.",
        about:
          "Cloud Career Accelerator starts with an assessment and treats the result as input to a generator rather than a score to display. Edge functions call Gemini to analyse the assessment, then produce a study plan, a weekly roadmap, and the learning material itself, each persisted so progress can be logged against it. Stripe checkout, a subscription check, and a customer portal gate the generated content and let subscribers manage billing themselves.",
        keyFeatures: [
          "Skills assessment analysed by an AI edge function",
          "Generated study plans, weekly roadmaps, and learning material",
          "Progress logging against persisted plans",
          "Stripe checkout with subscription status checks and customer portal",
          "Daily motivation generation as a scheduled touchpoint",
        ],
        challenges: [
          "Keeping AI output structured enough to persist as relational rows",
          "Gating generated content on live subscription state, not a stale flag",
          "Containing generation cost when several artefacts derive from one assessment",
        ],
      },
      id: {
        title: "Cloud Career Accelerator",
        description:
          "Pembimbing karier berbasis AI yang mengubah asesmen keterampilan menjadi rencana belajar, roadmap mingguan, dan materi, di balik langganan.",
        about:
          "Cloud Career Accelerator dimulai dari asesmen dan memperlakukan hasilnya sebagai masukan bagi generator, bukan sekadar skor untuk ditampilkan. Edge function memanggil Gemini untuk menganalisis asesmen, lalu menghasilkan rencana belajar, roadmap mingguan, dan materi pembelajarannya sendiri, masing-masing disimpan agar progres bisa dicatat terhadapnya. Checkout Stripe, pengecekan langganan, dan portal pelanggan membatasi akses konten sekaligus membiarkan pelanggan mengurus tagihannya sendiri.",
        keyFeatures: [
          "Asesmen keterampilan yang dianalisis oleh edge function AI",
          "Rencana belajar, roadmap mingguan, dan materi yang dihasilkan otomatis",
          "Pencatatan progres terhadap rencana yang tersimpan",
          "Checkout Stripe dengan pengecekan status langganan dan portal pelanggan",
          "Motivasi harian yang dihasilkan sebagai titik sentuh terjadwal",
        ],
        challenges: [
          "Menjaga keluaran AI cukup terstruktur untuk disimpan sebagai baris relasional",
          "Membatasi konten berdasarkan status langganan terkini, bukan penanda usang",
          "Menahan biaya generasi ketika banyak artefak lahir dari satu asesmen",
        ],
      },
    },
  },
  {
    id: "tcuphoto",
    link: "https://tcuphoto.lovable.app",
    thumbnail: tcuphotoShot,
    tags: ["Web", "Camera", "Photobooth", "Supabase"],
    category: "Web App / Media",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "Framer Motion", "Tailwind CSS"],
    copy: {
      en: {
        title: "Tcuphoto",
        description:
          "Browser photobooth: pick a template, shoot from the webcam, preview the composed strip, pay, then download and share.",
        about:
          "Tcuphoto runs a photobooth entirely in the browser. The flow is deliberately linear — template, capture, preview, download — because a booth is used standing up, often by someone who has never seen the interface before. Sessions and orders persist so a strip can be retrieved after the fact, and templates live as data rather than hard-coded layouts, so new frames ship without a code change.",
        keyFeatures: [
          "Template picker driven by a database table, not hard-coded layouts",
          "Webcam capture with a linear capture-to-download flow",
          "Composed strip preview before the download step",
          "Persisted photobooth sessions and orders for later retrieval",
          "Motion-driven transitions between booth steps",
        ],
        challenges: [
          "Handling camera permission refusal without dead-ending the flow",
          "Composing multi-shot strips client-side at print-usable resolution",
          "Keeping a stateful multi-step flow recoverable if the page reloads",
        ],
      },
      id: {
        title: "Tcuphoto",
        description:
          "Photobooth di peramban: pilih template, ambil foto dari webcam, tinjau hasil strip, bayar, lalu unduh dan bagikan.",
        about:
          "Tcuphoto menjalankan photobooth sepenuhnya di dalam peramban. Alurnya sengaja dibuat lurus — template, ambil gambar, pratinjau, unduh — karena booth dipakai sambil berdiri, sering oleh orang yang baru pertama melihat antarmukanya. Sesi dan pesanan tersimpan sehingga strip bisa diambil kembali belakangan, dan template disimpan sebagai data alih-alih tata letak yang ditulis di kode, jadi bingkai baru bisa dirilis tanpa mengubah kode.",
        keyFeatures: [
          "Pemilih template yang bersumber dari tabel basis data, bukan tata letak tetap",
          "Pengambilan gambar lewat webcam dengan alur lurus sampai unduhan",
          "Pratinjau strip tersusun sebelum tahap unduh",
          "Sesi photobooth dan pesanan tersimpan untuk diambil kemudian",
          "Transisi antar tahap booth dengan animasi gerak",
        ],
        challenges: [
          "Menangani penolakan izin kamera tanpa membuat alur buntu",
          "Menyusun strip multi-foto di sisi klien pada resolusi layak cetak",
          "Menjaga alur bertahap tetap bisa dipulihkan jika halaman dimuat ulang",
        ],
      },
    },
  },
  {
    id: "blessed-sport",
    link: "https://tcubersport.lovable.app",
    thumbnail: blessedSportShot,
    tags: ["Web", "Event Registration", "Admin panel", "Supabase"],
    category: "Event Platform",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "SheetJS", "Tailwind CSS"],
    copy: {
      en: {
        title: "Blessed Sport",
        description:
          "Sport event registration with an admin dashboard, email notifications on signup, spreadsheet export, and a bilingual interface.",
        about:
          "Blessed Sport handles the administrative side of running a sports event: participants register through a public form, organisers review them in a dashboard, and each signup fires a notification email through an edge function. Because organisers work from spreadsheets on the day, the registration table exports to XLSX directly. The interface carries a language toggle and theme switch so the same deployment serves both audiences.",
        keyFeatures: [
          "Public registration form writing to a registrations table",
          "Admin dashboard with role-gated access",
          "Notification email dispatched by an edge function on each signup",
          "XLSX export of the registration list for day-of use",
          "Bilingual interface with language and theme toggles",
        ],
        challenges: [
          "Guaranteeing a registration is saved even when the notification email fails",
          "Restricting participant data to organisers via row-level security",
          "Generating spreadsheet exports in-browser for a growing participant list",
        ],
      },
      id: {
        title: "Blessed Sport",
        description:
          "Pendaftaran acara olahraga dengan dashboard admin, notifikasi email saat pendaftaran, ekspor spreadsheet, dan antarmuka dwibahasa.",
        about:
          "Blessed Sport menangani sisi administratif penyelenggaraan acara olahraga: peserta mendaftar lewat formulir publik, panitia meninjaunya di dashboard, dan setiap pendaftaran memicu email notifikasi lewat edge function. Karena panitia bekerja dengan spreadsheet di hari pelaksanaan, tabel pendaftaran bisa diekspor langsung ke XLSX. Antarmukanya dilengkapi pengalih bahasa dan tema sehingga satu deployment melayani dua jenis pengguna.",
        keyFeatures: [
          "Formulir pendaftaran publik yang menulis ke tabel registrasi",
          "Dashboard admin dengan akses berbasis peran",
          "Email notifikasi dikirim edge function pada setiap pendaftaran",
          "Ekspor XLSX daftar peserta untuk dipakai di hari acara",
          "Antarmuka dwibahasa dengan pengalih bahasa dan tema",
        ],
        challenges: [
          "Memastikan pendaftaran tetap tersimpan meski email notifikasi gagal terkirim",
          "Membatasi data peserta hanya untuk panitia lewat row-level security",
          "Menghasilkan ekspor spreadsheet di peramban untuk daftar peserta yang terus bertambah",
        ],
      },
    },
  },
  {
    id: "olahraga-rohani-bareng",
    link: "https://sportswithgod.lovable.app",
    thumbnail: olahragaRohaniShot,
    tags: ["Web", "Event Registration", "Community", "Supabase"],
    category: "Event Platform",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "Tailwind CSS"],
    copy: {
      en: {
        title: "Olahraga Rohani Bareng",
        description:
          "Landing page and registration flow for a community sports ministry, with an admin dashboard and automated confirmation email.",
        about:
          "Olahraga Rohani Bareng pairs an invitational landing page with the machinery needed to actually run the signup. Visitors read the programme and register in the same scroll; organisers log in to a dashboard to review participants. A confirmation email goes out through an edge function, and a second function handles account removal so admins are not left editing the auth table by hand.",
        keyFeatures: [
          "Single-scroll landing page leading into the registration form",
          "Participant table with role-gated admin dashboard",
          "Registration confirmation email via edge function",
          "Admin-side user deletion handled server-side",
          "Password reset flow for organiser accounts",
        ],
        challenges: [
          "Keeping the signup form short enough that community members finish it",
          "Separating admin and participant reads with row-level security",
          "Deleting auth users safely from the client without exposing service keys",
        ],
      },
      id: {
        title: "Olahraga Rohani Bareng",
        description:
          "Landing page dan alur pendaftaran untuk pelayanan olahraga komunitas, dengan dashboard admin dan email konfirmasi otomatis.",
        about:
          "Olahraga Rohani Bareng memadukan halaman undangan dengan perangkat yang dibutuhkan untuk benar-benar menjalankan pendaftarannya. Pengunjung membaca programnya lalu mendaftar dalam satu gulungan halaman; panitia masuk ke dashboard untuk meninjau peserta. Email konfirmasi dikirim lewat edge function, dan fungsi kedua menangani penghapusan akun agar admin tidak perlu menyunting tabel autentikasi secara manual.",
        keyFeatures: [
          "Landing page satu gulungan yang menuntun ke formulir pendaftaran",
          "Tabel peserta dengan dashboard admin berbasis peran",
          "Email konfirmasi pendaftaran lewat edge function",
          "Penghapusan pengguna sisi admin yang ditangani di server",
          "Alur reset kata sandi untuk akun panitia",
        ],
        challenges: [
          "Menjaga formulir pendaftaran cukup ringkas agar anggota komunitas menyelesaikannya",
          "Memisahkan pembacaan data admin dan peserta dengan row-level security",
          "Menghapus akun autentikasi dengan aman dari klien tanpa membuka service key",
        ],
      },
    },
  },
  {
    id: "pempek-hoki-88",
    link: "https://pempekhoki88.lovable.app",
    thumbnail: pempekHoki88Shot,
    tags: ["Web", "F&B", "Ordering", "Supabase"],
    category: "F&B / E-commerce",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "PostgreSQL", "Tailwind CSS"],
    copy: {
      en: {
        title: "Pempek Hoki 88",
        description:
          "Storefront for a Palembang pempek business: product listing, order form routed to WhatsApp, and an admin panel for products and orders.",
        about:
          "Pempek Hoki 88 sells food where the transaction still finishes on WhatsApp, so the site is built to hand off cleanly rather than pretend to be a full checkout. Visitors browse products, read the ordering steps, and submit an order that is both stored and formatted into a WhatsApp message. The owner logs into an admin panel to manage products, review orders, and adjust site settings without touching code.",
        keyFeatures: [
          "Product catalogue managed from an admin panel",
          "Order form that persists the order and hands off to WhatsApp",
          "Order review screen for the shop owner",
          "Editable site settings stored as data",
          "Landing sections for how-to-order, reasons to buy, and testimonials",
        ],
        challenges: [
          "Keeping a stored order and its WhatsApp handoff message in sync",
          "Giving a non-technical owner catalogue control without a CMS",
          "Protecting the admin surface with roles rather than a shared password",
        ],
      },
      id: {
        title: "Pempek Hoki 88",
        description:
          "Etalase usaha pempek Palembang: daftar produk, formulir pesanan yang diteruskan ke WhatsApp, dan panel admin untuk produk dan pesanan.",
        about:
          "Pempek Hoki 88 menjual makanan yang transaksinya tetap diselesaikan lewat WhatsApp, jadi situsnya dirancang untuk mengoper dengan rapi, bukan berpura-pura menjadi checkout penuh. Pengunjung menelusuri produk, membaca langkah pemesanan, lalu mengirim pesanan yang sekaligus disimpan dan disusun menjadi pesan WhatsApp. Pemilik masuk ke panel admin untuk mengelola produk, meninjau pesanan, dan mengubah pengaturan situs tanpa menyentuh kode.",
        keyFeatures: [
          "Katalog produk yang dikelola dari panel admin",
          "Formulir pesanan yang menyimpan data sekaligus mengoper ke WhatsApp",
          "Layar peninjauan pesanan untuk pemilik toko",
          "Pengaturan situs yang bisa diubah dan disimpan sebagai data",
          "Bagian landing untuk cara pesan, alasan membeli, dan testimoni",
        ],
        challenges: [
          "Menjaga pesanan tersimpan tetap selaras dengan pesan WhatsApp yang dikirim",
          "Memberi pemilik non-teknis kendali katalog tanpa memasang CMS",
          "Melindungi area admin dengan peran, bukan kata sandi bersama",
        ],
      },
    },
  },
  {
    id: "csl-air-solution",
    link: "https://ciptasl.lovable.app",
    thumbnail: cslAirSolutionShot,
    tags: ["Web", "Landing page", "HVAC", "Tailwind CSS"],
    category: "Business Landing Page",
    role: "Frontend Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS"],
    copy: {
      en: {
        title: "CSL Air Solution",
        description:
          "Company profile site for an air conditioning and HVAC service provider, presenting services, coverage, and a direct contact path.",
        about:
          "CSL Air Solution is a company profile built for a trade where buyers arrive with an urgent problem. The site answers the qualifying questions first — what is serviced, at what scale, in which areas — before asking for contact, so an enquiry arrives already scoped. The layout stays deliberately plain and fast, because much of this traffic comes from a phone while a unit is already broken.",
        keyFeatures: [
          "Service breakdown covering installation, maintenance, and repair",
          "Company profile and capability sections for corporate buyers",
          "Direct contact path surfaced throughout the page",
          "Responsive layout tuned for mobile-first traffic",
          "Fast static delivery with no dependency on a backend",
        ],
        challenges: [
          "Communicating technical service scope without trade jargon",
          "Keeping load fast for visitors on mobile data",
          "Presenting one page credibly to both household and corporate buyers",
        ],
      },
      id: {
        title: "CSL Air Solution",
        description:
          "Situs profil perusahaan penyedia jasa pendingin udara dan HVAC, menampilkan layanan, cakupan wilayah, dan jalur kontak langsung.",
        about:
          "CSL Air Solution adalah profil perusahaan yang dibangun untuk bidang di mana calon pelanggan datang dengan masalah mendesak. Situsnya menjawab pertanyaan penyaring lebih dulu — apa yang dilayani, pada skala berapa, di wilayah mana — sebelum meminta kontak, sehingga permintaan yang masuk sudah jelas cakupannya. Tata letaknya sengaja dibuat polos dan cepat, karena sebagian besar kunjungan datang dari ponsel saat unit sudah rusak.",
        keyFeatures: [
          "Rincian layanan mencakup pemasangan, perawatan, dan perbaikan",
          "Bagian profil dan kapabilitas perusahaan untuk pembeli korporat",
          "Jalur kontak langsung yang muncul di sepanjang halaman",
          "Tata letak responsif yang disetel untuk kunjungan dari ponsel",
          "Pengiriman statis yang cepat tanpa bergantung pada backend",
        ],
        challenges: [
          "Menyampaikan cakupan layanan teknis tanpa jargon bengkel",
          "Menjaga waktu muat tetap cepat untuk pengunjung dengan data seluler",
          "Menyajikan satu halaman yang meyakinkan bagi pembeli rumahan sekaligus korporat",
        ],
      },
    },
  },
  {
    id: "atelier",
    link: "https://atelierwear.lovable.app",
    thumbnail: atelierShot,
    tags: ["Web", "Landing page", "Fashion", "Tailwind CSS"],
    category: "Brand Landing Page",
    role: "Frontend Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS"],
    copy: {
      en: {
        title: "Atelier",
        description:
          "Brand page for an apparel label, built around large imagery, generous whitespace, and a restrained editorial type scale.",
        about:
          "Atelier is a fashion brand front page where the design carries the product rather than describing it. Layout choices lean editorial — wide margins, a narrow type scale, imagery given room — because the goal is to establish a label's tone before any commerce mechanics appear. The result is a page that stays quiet on purpose and lets the photography set the register.",
        keyFeatures: [
          "Editorial layout with generous whitespace and large imagery",
          "Restrained type scale carrying the brand tone",
          "Responsive composition that holds its proportions on mobile",
          "Single-page structure with anchored section navigation",
          "Static delivery with no backend dependency",
        ],
        challenges: [
          "Holding editorial proportions when the viewport narrows",
          "Keeping large photography fast without visibly degrading it",
          "Designing restraint that still guides the eye through the page",
        ],
      },
      id: {
        title: "Atelier",
        description:
          "Halaman merek untuk label pakaian, dibangun di sekitar gambar berukuran besar, ruang kosong yang lapang, dan skala tipografi editorial yang tenang.",
        about:
          "Atelier adalah halaman depan merek fesyen di mana desainnya memikul produk, bukan menjelaskannya. Pilihan tata letaknya bernuansa editorial — margin lebar, skala huruf yang sempit, gambar diberi ruang — karena tujuannya membangun nada sebuah label sebelum mekanisme jual beli muncul. Hasilnya halaman yang sengaja bersuara pelan dan membiarkan fotografi yang menentukan nadanya.",
        keyFeatures: [
          "Tata letak editorial dengan ruang kosong lapang dan gambar besar",
          "Skala tipografi yang tenang untuk memikul nada merek",
          "Komposisi responsif yang menjaga proporsinya di layar ponsel",
          "Struktur satu halaman dengan navigasi antar bagian berjangkar",
          "Pengiriman statis tanpa ketergantungan pada backend",
        ],
        challenges: [
          "Menjaga proporsi editorial ketika lebar layar menyempit",
          "Membuat fotografi besar tetap cepat tanpa terlihat menurun kualitasnya",
          "Merancang ketenangan yang tetap menuntun mata menyusuri halaman",
        ],
      },
    },
  },
  {
    id: "tcuber-agency",
    link: "https://tcuberagency.vercel.app",
    thumbnail: tcuberAgencyShot,
    tags: ["Web", "Landing page", "Agency", "Tailwind CSS"],
    category: "Agency Landing Page",
    role: "Frontend Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS"],
    copy: {
      en: {
        title: "Tcuber Agency",
        description:
          "Service page for a creative agency offering video editing and copywriting, leading from capability to portfolio to enquiry.",
        about:
          "Tcuber Agency presents a two-service creative studio in the order a prospective client evaluates one: what is offered, evidence it has been done, who is behind it, then how to make contact. Keeping the page to that sequence avoids the common agency-site failure of describing process at length before showing any work.",
        keyFeatures: [
          "Service breakdown for video editing and copywriting",
          "Portfolio section placed before the about and contact sections",
          "Direct enquiry path at the end of the scroll",
          "Responsive single-page layout with section anchors",
          "Static delivery with no backend dependency",
        ],
        challenges: [
          "Presenting two unrelated services without diluting either",
          "Showing video work credibly on a static page",
          "Sequencing sections so proof arrives before the pitch",
        ],
      },
      id: {
        title: "Tcuber Agency",
        description:
          "Halaman layanan untuk agensi kreatif bidang video editing dan copywriting, menuntun dari kapabilitas ke portofolio lalu ke permintaan kerja sama.",
        about:
          "Tcuber Agency menyajikan studio kreatif dua layanan dalam urutan yang dipakai calon klien saat menilainya: apa yang ditawarkan, bukti bahwa itu pernah dikerjakan, siapa yang mengerjakan, lalu cara menghubungi. Menjaga halaman pada urutan itu menghindari kegagalan umum situs agensi yang menjelaskan proses panjang lebar sebelum menunjukkan satu pun hasil kerja.",
        keyFeatures: [
          "Rincian layanan untuk video editing dan copywriting",
          "Bagian portofolio diletakkan sebelum bagian tentang dan kontak",
          "Jalur permintaan kerja sama langsung di akhir halaman",
          "Tata letak satu halaman responsif dengan jangkar antar bagian",
          "Pengiriman statis tanpa ketergantungan backend",
        ],
        challenges: [
          "Menyajikan dua layanan yang tidak berkaitan tanpa melemahkan keduanya",
          "Menampilkan hasil kerja video secara meyakinkan di halaman statis",
          "Mengurutkan bagian agar bukti muncul sebelum penawaran",
        ],
      },
    },
  },
  {
    id: "tcuberverse",
    link: "https://tcuberverse.vercel.app",
    thumbnail: tcuberverseShot,
    tags: ["Web", "Landing page", "Personal brand", "Tailwind CSS"],
    category: "Personal Brand Site",
    role: "Frontend Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS"],
    copy: {
      en: {
        title: "Tcuberverse",
        description:
          "Personal brand page positioning data engineering and AI services, with light and dark themes and a direct contact path.",
        about:
          "Tcuberverse is a personal positioning page rather than a portfolio: it states a specialism, lists what can be engaged, and provides a route to contact. It ships a theme provider so the page respects the visitor's system preference, which matters for an audience that mostly reads technical material in dark mode.",
        keyFeatures: [
          "Positioning statement around data engineering and AI",
          "Service list framed as engagements rather than skills",
          "Light and dark theming driven by a theme provider",
          "Direct contact section closing the page",
          "Responsive single-page layout",
        ],
        challenges: [
          "Stating a specialism precisely without narrowing the audience",
          "Keeping both themes legible from one token set",
          "Making a short page feel substantial rather than thin",
        ],
      },
      id: {
        title: "Tcuberverse",
        description:
          "Halaman personal branding yang memposisikan layanan data engineering dan AI, dengan tema terang dan gelap serta jalur kontak langsung.",
        about:
          "Tcuberverse adalah halaman pemosisian diri, bukan portofolio: ia menyatakan bidang keahlian, mendaftar apa yang bisa dikerjasamakan, dan menyediakan jalur untuk menghubungi. Halaman ini memuat theme provider agar tampilan mengikuti preferensi sistem pengunjung, hal yang penting bagi audiens yang sebagian besar membaca materi teknis dalam mode gelap.",
        keyFeatures: [
          "Pernyataan pemosisian di bidang data engineering dan AI",
          "Daftar layanan yang dibingkai sebagai bentuk kerja sama, bukan sekadar keahlian",
          "Tema terang dan gelap yang dikendalikan theme provider",
          "Bagian kontak langsung sebagai penutup halaman",
          "Tata letak satu halaman yang responsif",
        ],
        challenges: [
          "Menyatakan bidang keahlian dengan tepat tanpa mempersempit audiens",
          "Menjaga kedua tema tetap terbaca dari satu set token warna",
          "Membuat halaman pendek terasa berisi, bukan seadanya",
        ],
      },
    },
  },
  {
    id: "royal-chess",
    link: "https://mobile-king-quest.lovable.app",
    thumbnail: royalChessShot,
    tags: ["Web", "Game", "Chess", "Supabase"],
    category: "Game / Web App",
    role: "Full Stack Developer",
    technologies: ["React", "TypeScript", "Vite", "Supabase", "Tailwind CSS"],
    copy: {
      en: {
        title: "Royal Chess",
        description:
          "Browser chess with a hand-built board, legal move handling, selectable time controls, a game clock, and authenticated profiles.",
        about:
          "Royal Chess implements the board itself rather than embedding a chess widget: squares, piece state, move legality, and turn handling are all application code. On top of that sits a clock with selectable time controls, so a game can be played at blitz or classical pace, and an authenticated profile that persists a player across sessions.",
        keyFeatures: [
          "Board and square components built from scratch, not embedded",
          "Legal move handling and turn state managed in application code",
          "Game clock with a selectable time control picker",
          "Authenticated profiles persisting player identity",
          "Game menu for starting and configuring a match",
        ],
        challenges: [
          "Encoding chess move legality correctly, including the special cases",
          "Keeping clock state accurate across re-renders and tab switches",
          "Designing a board that stays playable on a narrow screen",
        ],
      },
      id: {
        title: "Royal Chess",
        description:
          "Catur di peramban dengan papan buatan sendiri, penanganan langkah legal, kontrol waktu yang bisa dipilih, jam pertandingan, dan profil terautentikasi.",
        about:
          "Royal Chess membangun papannya sendiri alih-alih menyematkan widget catur: kotak, keadaan bidak, keabsahan langkah, dan pergantian giliran semuanya ditulis sebagai kode aplikasi. Di atasnya terdapat jam dengan pilihan kontrol waktu, sehingga permainan bisa dijalankan dalam tempo blitz maupun klasik, serta profil terautentikasi yang menjaga identitas pemain antar sesi.",
        keyFeatures: [
          "Komponen papan dan kotak dibangun dari nol, bukan sematan",
          "Penanganan langkah legal dan giliran dikelola di kode aplikasi",
          "Jam pertandingan dengan pemilih kontrol waktu",
          "Profil terautentikasi yang menyimpan identitas pemain",
          "Menu permainan untuk memulai dan mengatur pertandingan",
        ],
        challenges: [
          "Menuliskan aturan keabsahan langkah catur dengan benar, termasuk kasus khususnya",
          "Menjaga keadaan jam tetap akurat melintasi render ulang dan perpindahan tab",
          "Merancang papan yang tetap bisa dimainkan di layar sempit",
        ],
      },
    },
  },
  {
    id: "new-year-photo-booth",
    link: null,
    thumbnail: newYearPhotoBoothShot,
    tags: ["Web", "Camera", "Photobooth", "Canvas"],
    category: "Web App / Media",
    role: "Frontend Developer",
    technologies: ["React", "TypeScript", "Vite", "Web Camera API", "Tailwind CSS"],
    copy: {
      en: {
        title: "New Year Photo Booth 2025",
        description:
          "Themed photo booth for New Year: countdown capture, frame selection, sticker editing, and a locked photo that unlocks at midnight.",
        about:
          "A seasonal photo booth built around one idea: a photo taken before midnight stays sealed until the year turns. Capture runs through a countdown so subjects can pose, frames and stickers are applied client-side, and the finished image is held behind a locked state with its own view route. The particle background and countdown exist to make waiting part of the experience rather than dead time.",
        keyFeatures: [
          "Countdown-timed webcam capture",
          "Frame selection and sticker editing applied client-side",
          "Locked photo state with a dedicated view route",
          "Animated particle background tied to the countdown",
          "Shareable link to a captured photo",
        ],
        challenges: [
          "Compositing frames and stickers onto a captured frame in the browser",
          "Enforcing an unlock time the client cannot trivially bypass",
          "Keeping the camera preview smooth alongside an animated background",
        ],
      },
      id: {
        title: "New Year Photo Booth 2025",
        description:
          "Photo booth bertema Tahun Baru: pengambilan gambar dengan hitung mundur, pemilihan bingkai, penyuntingan stiker, dan foto terkunci yang terbuka saat tengah malam.",
        about:
          "Photo booth musiman yang dibangun di sekitar satu gagasan: foto yang diambil sebelum tengah malam tetap tersegel sampai tahun berganti. Pengambilan gambar berjalan lewat hitung mundur agar subjek sempat berpose, bingkai dan stiker diterapkan di sisi klien, dan gambar jadinya ditahan dalam keadaan terkunci dengan rute tampilannya sendiri. Latar partikel dan hitung mundur ada supaya menunggu menjadi bagian dari pengalaman, bukan waktu kosong.",
        keyFeatures: [
          "Pengambilan gambar webcam dengan hitung mundur",
          "Pemilihan bingkai dan penyuntingan stiker di sisi klien",
          "Keadaan foto terkunci dengan rute tampilan tersendiri",
          "Latar partikel beranimasi yang terhubung dengan hitung mundur",
          "Tautan berbagi menuju foto yang sudah diambil",
        ],
        challenges: [
          "Menggabungkan bingkai dan stiker ke atas gambar tangkapan di peramban",
          "Menerapkan waktu pembukaan yang tidak mudah dilewati dari sisi klien",
          "Menjaga pratinjau kamera tetap mulus bersamaan dengan latar beranimasi",
        ],
      },
    },
  },
  {
    id: "swifties-corner",
    link: "https://swiftiescorner.lovable.app",
    thumbnail: swiftiesCornerShot,
    tags: ["Web", "Fan site", "Content", "Tailwind CSS"],
    category: "Content / Fan Site",
    role: "Frontend Developer",
    technologies: ["React", "TypeScript", "Vite", "Tailwind CSS"],
    copy: {
      en: {
        title: "Swifties Corner",
        description:
          "Tribute site covering a discography and the eras that frame it, organised as a browsable timeline rather than a flat list.",
        about:
          "Swifties Corner is a content-structure exercise dressed as a fan site. The material — a discography and a sequence of visually distinct eras — is inherently chronological, so the page treats era as the organising axis and lets each one carry its own visual register while staying inside one coherent design system.",
        keyFeatures: [
          "Discography section organised chronologically",
          "Eras section where each era carries its own visual treatment",
          "Biography and about content woven into the timeline",
          "Sticky navigation across a long single-page scroll",
          "Responsive layout holding structure on mobile",
        ],
        challenges: [
          "Letting each era look distinct without fragmenting the design system",
          "Keeping a long single-page scroll navigable",
          "Structuring dense reference content so it stays scannable",
        ],
      },
      id: {
        title: "Swifties Corner",
        description:
          "Situs penghormatan yang memuat diskografi dan era-era yang membingkainya, disusun sebagai lini masa yang bisa ditelusuri, bukan daftar datar.",
        about:
          "Swifties Corner adalah latihan penataan konten yang dikemas sebagai situs penggemar. Materinya — sebuah diskografi dan rangkaian era yang secara visual berbeda-beda — pada dasarnya bersifat kronologis, jadi halaman ini menjadikan era sebagai sumbu penataan dan membiarkan tiap era membawa nuansa visualnya sendiri tanpa keluar dari satu sistem desain yang utuh.",
        keyFeatures: [
          "Bagian diskografi yang disusun secara kronologis",
          "Bagian era di mana tiap era membawa perlakuan visualnya sendiri",
          "Konten biografi dan profil yang dijalin ke dalam lini masa",
          "Navigasi lengket sepanjang gulungan satu halaman yang panjang",
          "Tata letak responsif yang menjaga struktur di layar ponsel",
        ],
        challenges: [
          "Membiarkan tiap era tampil berbeda tanpa memecah sistem desain",
          "Menjaga gulungan satu halaman yang panjang tetap mudah dinavigasi",
          "Menyusun konten referensi yang padat agar tetap mudah dipindai",
        ],
      },
    },
  },
  {
    id: "simple-twitter",
    link: null,
    thumbnail: simpleTwitterShot,
    tags: ["Backend", "Go", "Distributed systems", "PostgreSQL"],
    category: "Backend / Infrastructure",
    role: "Backend Developer",
    technologies: ["Go", "PostgreSQL", "Redis", "NATS", "AWS S3", "Prometheus"],
    copy: {
      en: {
        title: "Simple Twitter",
        description:
          "Go microblogging backend wired for production concerns: pluggable cache and storage, a NATS-driven mail worker, S3 media, and Prometheus metrics.",
        about:
          "Simple Twitter is a backend study rather than a UI one. The service is assembled from swappable adapters — cache, database, object storage, and message queue each register themselves behind an interface, selected at boot from environment configuration. A NATS-backed worker handles outbound mail off the request path, media goes to S3 with credentials pulled from Secrets Manager, and Prometheus metrics run alongside the HTTP server so the process is observable from the start.",
        keyFeatures: [
          "Pluggable cache, database, and storage adapters selected at runtime",
          "NATS-driven background worker for outbound mail",
          "S3 media storage with credentials from AWS Secrets Manager",
          "Prometheus metrics served alongside the HTTP server",
          "Structured logging with zerolog and server-rendered templates",
        ],
        challenges: [
          "Designing adapter interfaces general enough to swap implementations at boot",
          "Keeping mail delivery off the request path without losing failures silently",
          "Coordinating Postgres, Redis, NATS, and S3 in one startup sequence",
        ],
      },
      id: {
        title: "Simple Twitter",
        description:
          "Backend microblogging berbasis Go yang disiapkan untuk kebutuhan produksi: cache dan storage yang bisa ditukar, worker email lewat NATS, media di S3, dan metrik Prometheus.",
        about:
          "Simple Twitter adalah studi backend, bukan studi antarmuka. Layanannya dirakit dari adapter yang bisa ditukar — cache, basis data, object storage, dan message queue masing-masing mendaftarkan diri di balik sebuah interface, lalu dipilih saat boot dari konfigurasi environment. Worker berbasis NATS menangani pengiriman email di luar jalur request, media disimpan di S3 dengan kredensial yang diambil dari Secrets Manager, dan metrik Prometheus berjalan berdampingan dengan server HTTP sehingga prosesnya bisa diamati sejak awal.",
        keyFeatures: [
          "Adapter cache, basis data, dan storage yang bisa ditukar saat runtime",
          "Worker latar belakang berbasis NATS untuk pengiriman email",
          "Penyimpanan media di S3 dengan kredensial dari AWS Secrets Manager",
          "Metrik Prometheus yang disajikan berdampingan dengan server HTTP",
          "Logging terstruktur dengan zerolog dan template yang dirender di server",
        ],
        challenges: [
          "Merancang interface adapter yang cukup umum untuk ditukar implementasinya saat boot",
          "Menjaga pengiriman email di luar jalur request tanpa kehilangan kegagalan secara diam-diam",
          "Mengoordinasikan Postgres, Redis, NATS, dan S3 dalam satu urutan startup",
        ],
      },
    },
  },
  {
    id: "habit-tracker",
    link: null,
    thumbnail: habitTrackerShot,
    tags: ["iOS", "SwiftUI", "Clean Architecture", "Testing"],
    category: "Mobile App",
    role: "iOS Developer",
    technologies: ["Swift", "SwiftUI", "SwiftData", "Swift Testing"],
    copy: {
      en: {
        title: "Habit Tracker",
        description:
          "SwiftUI habit tracker structured in clean architecture layers, with streak and completion-rate use cases covered by unit tests.",
        about:
          "Habit Tracker is an architecture exercise built as a working app. Domain logic — habits, completions, frequencies, streaks, and statistics — sits in plain Swift types with no framework imports, reached through a repository protocol. SwiftData backs it in production while an in-memory repository backs it in tests, which is what lets the streak and completion-rate use cases be unit tested directly. Development runs through a staging branch merged by pull request, and the codebase was updated to clear Swift 6 actor-isolation warnings.",
        keyFeatures: [
          "Domain layer of plain Swift types, independent of SwiftUI and SwiftData",
          "Use cases for streak calculation, completion rate, and statistics",
          "Repository protocol with SwiftData and in-memory implementations",
          "Unit tests covering domain models and every use case",
          "Staging-to-main pull request flow, Swift 6 concurrency clean",
        ],
        challenges: [
          "Calculating streaks correctly across differing habit frequencies",
          "Keeping the domain layer free of persistence framework types",
          "Resolving Swift 6 actor-isolation warnings without loosening the model",
        ],
      },
      id: {
        title: "Habit Tracker",
        description:
          "Pelacak kebiasaan berbasis SwiftUI yang disusun dalam lapisan clean architecture, dengan use case streak dan tingkat penyelesaian yang diuji unit test.",
        about:
          "Habit Tracker adalah latihan arsitektur yang dibangun sebagai aplikasi yang benar-benar berjalan. Logika domain — kebiasaan, penyelesaian, frekuensi, streak, dan statistik — berada pada tipe Swift biasa tanpa impor framework, dan diakses lewat protokol repository. SwiftData menopangnya di produksi sementara repository in-memory menopangnya saat pengujian, dan justru itulah yang membuat use case streak dan tingkat penyelesaian bisa diuji unit secara langsung. Pengembangan berjalan lewat branch staging yang digabung melalui pull request, dan kodenya diperbarui untuk membersihkan peringatan actor-isolation Swift 6.",
        keyFeatures: [
          "Lapisan domain berisi tipe Swift biasa, lepas dari SwiftUI dan SwiftData",
          "Use case untuk perhitungan streak, tingkat penyelesaian, dan statistik",
          "Protokol repository dengan implementasi SwiftData dan in-memory",
          "Unit test yang mencakup model domain dan seluruh use case",
          "Alur pull request staging ke main, bersih dari peringatan konkurensi Swift 6",
        ],
        challenges: [
          "Menghitung streak dengan benar untuk frekuensi kebiasaan yang berbeda-beda",
          "Menjaga lapisan domain bebas dari tipe framework persistensi",
          "Menyelesaikan peringatan actor-isolation Swift 6 tanpa melonggarkan model",
        ],
      },
    },
  },
  {
    id: "web-catalog-prototype",
    link: null,
    thumbnail: webCatalogShot,
    tags: ["Python", "Streamlit", "Snowflake", "Data app"],
    category: "Data App / Prototype",
    role: "Data Engineer",
    technologies: ["Python", "Streamlit", "Snowflake", "pandas"],
    copy: {
      en: {
        title: "Web Catalog Prototype",
        description:
          "Streamlit product catalogue reading live from Snowflake, turning a warehouse table into a browsable storefront without a web stack.",
        about:
          "A prototype answering a data-side question: how quickly can a warehouse table become something a non-analyst can browse? The app queries Snowflake directly, loads the result through pandas, and renders a product picker where selecting a variant fetches its image, price, sizes, and upsell copy in a second parameterised query. Credentials stay in Streamlit secrets rather than the source, and the whole storefront is a single Python file.",
        keyFeatures: [
          "Live Snowflake queries driving the catalogue, no static data",
          "Variant picker triggering a parameterised detail lookup",
          "Product image, price, size list, and upsell copy from the warehouse",
          "Credentials held in Streamlit secrets, not in source",
          "Entire storefront contained in one Python file",
        ],
        challenges: [
          "Keeping warehouse round-trips low enough for interactive use",
          "Parameterising queries safely against user-selected input",
          "Presenting warehouse columns as a storefront without a frontend stack",
        ],
      },
      id: {
        title: "Web Catalog Prototype",
        description:
          "Katalog produk berbasis Streamlit yang membaca langsung dari Snowflake, mengubah tabel gudang data menjadi etalase yang bisa ditelusuri tanpa stack web.",
        about:
          "Prototipe yang menjawab pertanyaan dari sisi data: secepat apa sebuah tabel gudang data bisa menjadi sesuatu yang bisa ditelusuri orang non-analis? Aplikasi ini melakukan kueri langsung ke Snowflake, memuat hasilnya lewat pandas, dan menampilkan pemilih produk di mana memilih satu varian akan mengambil gambar, harga, ukuran, dan teks penawaran tambahannya lewat kueri berparameter kedua. Kredensial disimpan di Streamlit secrets, bukan di dalam kode, dan seluruh etalasenya berupa satu berkas Python.",
        keyFeatures: [
          "Kueri Snowflake langsung sebagai sumber katalog, tanpa data statis",
          "Pemilih varian yang memicu pencarian detail berparameter",
          "Gambar produk, harga, daftar ukuran, dan teks penawaran dari gudang data",
          "Kredensial disimpan di Streamlit secrets, bukan di dalam kode",
          "Seluruh etalase termuat dalam satu berkas Python",
        ],
        challenges: [
          "Menjaga jumlah panggilan ke gudang data tetap rendah agar tetap interaktif",
          "Menyusun kueri berparameter dengan aman terhadap masukan pilihan pengguna",
          "Menyajikan kolom gudang data sebagai etalase tanpa stack frontend",
        ],
      },
    },
  },
];

export const PROJECT_GROUPS = [
  "Business Systems",
  "Platforms & Communities",
  "Landing & Brand",
  "Engineering & Data",
  "Games & Interactive",
  "Commerce & Booking",
] as const;

export type ProjectGroup = (typeof PROJECT_GROUPS)[number];

// Grouping axis for the portfolio grid. Deliberately coarser than each project's
// own `category`, which stays as-is and still shows on the detail page.
// Covers the three projects hardcoded in Portfolio.tsx as well as EXTRA_PROJECTS.
const GROUP_BY_ID: Record<string, ProjectGroup> = {
  // Business Systems
  fintcu: "Business Systems",
  sigmahub: "Business Systems",
  invoiceku: "Business Systems",
  "sales-tracker": "Business Systems",
  "tcu-project": "Business Systems",
  "monica-house": "Business Systems",
  tsecure: "Business Systems",
  "tcu-analytics": "Business Systems",
  "cg-schedule-pro": "Business Systems",
  "dokum-gsc-2026": "Business Systems",

  // Platforms & Communities
  "tcuber-academy": "Platforms & Communities",
  "cloud-career-accelerator": "Platforms & Communities",
  tcustream: "Platforms & Communities",
  "liveprod-cp": "Platforms & Communities",
  "cg-ya124": "Platforms & Communities",
  "blessed-sport": "Platforms & Communities",
  "olahraga-rohani-bareng": "Platforms & Communities",
  "swifties-corner": "Platforms & Communities",

  // Landing & Brand
  paumedika: "Landing & Brand",
  "sendy-tg-job-center": "Landing & Brand",
  "csl-air-solution": "Landing & Brand",
  atelier: "Landing & Brand",
  "tcuber-agency": "Landing & Brand",
  tcuberverse: "Landing & Brand",
  "ayam-juara": "Landing & Brand",
  mapanfurniture: "Landing & Brand",

  // Engineering & Data
  "simple-twitter": "Engineering & Data",
  "habit-tracker": "Engineering & Data",
  "web-catalog-prototype": "Engineering & Data",
  splitbill: "Engineering & Data",
  "pelatihan-vba-sql-powerbi": "Engineering & Data",
  "penguin-analysis": "Engineering & Data",

  // Games & Interactive
  "slay-heroes": "Games & Interactive",
  "royal-chess": "Games & Interactive",
  tcuphoto: "Games & Interactive",
  "new-year-photo-booth": "Games & Interactive",

  // Commerce & Booking
  flytcu: "Commerce & Booking",
  "pempek-hoki-88": "Commerce & Booking",
  tcuberstore: "Commerce & Booking",
};

/** Falls back to Engineering & Data so an unmapped project still renders somewhere. */
export const groupForProject = (id: string): ProjectGroup =>
  GROUP_BY_ID[id] ?? "Engineering & Data";

export const resolveProject = (project: ProjectDefinition, language: Language): ResolvedProject => {
  const { copy, ...rest } = project;
  const text = language === "id" ? copy.id : copy.en;
  return {
    ...rest,
    ...text,
    thumbnail: project.thumbnail ?? placeholderThumbnail(text.title, project.id),
    galleryImages: project.galleryImages ?? [],
  };
};

export const getExtraProjects = (language: Language): ResolvedProject[] =>
  EXTRA_PROJECTS.map((project) => resolveProject(project, language));
